import React, { useState, useEffect, useRef, useCallback } from 'react';
import img from '../../../assests/images/Images';

const SLIDES = [
  {
    id: 'custom',
    image: img.customslide,
    alt: 'Custom Software Engineering & Tailored Web Applications',
    tag: 'Custom Web Apps',
    title: 'Custom Software Development',
    caption: 'Scalable web applications tailored to streamline operations.',
  },
  {
    id: 'uiux',
    image: img.uiuxslide,
    alt: 'UI/UX Design & Prototyping',
    tag: 'UI/UX Experience',
    title: 'Intuitive Experience Design',
    caption: 'User-first wireframing, high-fidelity prototypes & clean interfaces.',
  },
  {
    id: 'digital',
    image: img.digitalslide,
    alt: 'Digital Marketing & Growth Analytics',
    tag: 'Digital Growth',
    title: 'Data-Driven Growth & Marketing',
    caption: 'Conversion-focused campaigns, analytics & customer acquisition.',
  },
  {
    id: 'devops',
    image: img.devopsslide,
    alt: 'Cloud DevOps & High Availability Infrastructure',
    tag: 'Cloud & DevOps',
    title: 'Enterprise Cloud Infrastructure',
    caption: 'Ultra-scale server architecture, automated CI/CD & cloud reliability.',
  },
  {
    id: 'business',
    image: img.businessslide,
    alt: 'Business Solutions & Enterprise ERP Systems',
    tag: 'Business Systems',
    title: 'Enterprise ERP & Operations',
    caption: 'End-to-end management dashboards, workflows & unified reporting.',
  },
];

const AUTOPLAY_INTERVAL = 4200; // 4.2 seconds per slide

const HeroSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const timerRef = useRef(null);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % SLIDES.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + SLIDES.length) % SLIDES.length);
  }, []);

  const goToSlide = (idx) => {
    setCurrentIndex(idx);
  };

  // Autoplay timer
  useEffect(() => {
    if (isPaused) return;

    timerRef.current = setInterval(() => {
      nextSlide();
    }, AUTOPLAY_INTERVAL);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [nextSlide, isPaused, currentIndex]);

  // Touch Swipe handlers
  const minSwipeDistance = 40;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowRight') {
      nextSlide();
    } else if (e.key === 'ArrowLeft') {
      prevSlide();
    }
  };

  return (
    <div
      className="sipl-hero-slider-wrapper w-100"
      tabIndex={0}
      role="region"
      aria-label="Capabilities Showcase Slider"
      aria-roledescription="carousel"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onKeyDown={handleKeyDown}
      style={{
        outline: 'none',
      }}
    >
      {/* ── Slide Track ── */}
      <div
        className="sipl-hero-slide-track d-flex h-100 w-100"
        style={{
          transform: `translateX(-${currentIndex * 100}%)`,
          transition: 'transform 0.65s cubic-bezier(0.22, 1, 0.36, 1)',
          willChange: 'transform',
          height: '100%',
        }}
      >
        {SLIDES.map((slide, idx) => (
          <div
            key={slide.id}
            className="sipl-hero-slide-item flex-shrink-0 w-100 h-100 position-relative"
            aria-hidden={idx !== currentIndex}
            style={{
              height: '100%',
              backgroundColor: 'transparent',
              overflow: 'hidden',
            }}
          >
            {/* Full-Cover Image with subtle zoom */}
            <img
              src={slide.image}
              alt={slide.alt}
              loading={idx === 0 ? 'eager' : 'lazy'}
              fetchPriority={idx === 0 ? 'high' : 'auto'}
              decoding="async"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
                display: 'block',
                transform: 'scale(1.02)',
              }}
            />

            {/* Gradient Overlay for quote legibility */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(180deg, rgba(15, 23, 42, 0.1) 0%, rgba(15, 23, 42, 0.0) 40%, rgba(15, 23, 42, 0.78) 100%)',
                pointerEvents: 'none',
              }}
            />

            {/* Slide Quotes & Information Overlay */}
            <div
              style={{
                position: 'absolute',
                bottom: '36px',
                left: '24px',
                right: '24px',
                zIndex: 2,
                color: '#ffffff',
                pointerEvents: 'none',
              }}
            >
              <div className="d-flex align-items-center gap-2 mb-1.5">
                <span
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.22)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    color: '#ffffff',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    padding: '3px 10px',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                  }}
                >
                  {slide.tag}
                </span>
              </div>
              <h3
                style={{
                  fontSize: 'clamp(1.05rem, 1.7vw, 1.35rem)',
                  fontWeight: 600,
                  margin: '0 0 4px 0',
                  color: '#ffffff',
                  textShadow: '0 2px 8px rgba(0, 0, 0, 0.6)',
                }}
              >
                {slide.title}
              </h3>
              <p
                className="d-none d-sm-block mb-0"
                style={{
                  fontSize: '0.85rem',
                  color: 'rgba(241, 245, 249, 0.94)',
                  maxWidth: '520px',
                  textShadow: '0 1px 4px rgba(0, 0, 0, 0.7)',
                  lineHeight: 1.45,
                }}
              >
                {slide.caption}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Top-Right Counter Pill ── */}
      <div
        style={{
          position: 'absolute',
          top: '14px',
          right: '14px',
          zIndex: 3,
          backgroundColor: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          color: '#ffffff',
          fontSize: '0.72rem',
          fontWeight: 600,
          padding: '3px 10px',
          borderRadius: '8px',
          letterSpacing: '0.04em',
        }}
      >
        <span style={{ color: '#38bdf8' }}>{String(currentIndex + 1).padStart(2, '0')}</span>
        <span style={{ opacity: 0.6, margin: '0 4px' }}>/</span>
        <span>{String(SLIDES.length).padStart(2, '0')}</span>
      </div>

      {/* ── Top Progress Line ── */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          zIndex: 4,
        }}
      >
        <div
          key={currentIndex}
          style={{
            height: '100%',
            backgroundColor: '#0284c7',
            width: isPaused ? `${((currentIndex + 1) / SLIDES.length) * 100}%` : '100%',
            animation: !isPaused ? `siplSliderProgress ${AUTOPLAY_INTERVAL}ms linear forwards` : 'none',
            transformOrigin: 'left',
          }}
        />
      </div>

      {/* ── Bottom Interactive Dots / Indicators ── */}
      <div
        className="d-flex align-items-center justify-content-center gap-1.5"
        style={{
          position: 'absolute',
          bottom: '12px',
          left: 0,
          right: 0,
          zIndex: 3,
          pointerEvents: 'auto',
        }}
      >
        {SLIDES.map((slide, idx) => {
          const isActive = idx === currentIndex;
          return (
            <button
              key={slide.id}
              type="button"
              onClick={() => goToSlide(idx)}
              aria-label={`Go to slide ${idx + 1}`}
              aria-current={isActive}
              style={{
                width: isActive ? '24px' : '8px',
                height: '6px',
                borderRadius: '4px',
                backgroundColor: isActive ? '#38bdf8' : 'rgba(255, 255, 255, 0.45)',
                border: 'none',
                padding: 0,
                margin: '0 2px',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
                boxShadow: isActive ? '0 0 8px rgba(56, 189, 248, 0.6)' : 'none',
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

export default HeroSlider;
